import { Check, X, Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useRelativeTime } from '@/i18n/useRelativeTime';
import type { EmailThreadCompanyConnection } from '@/types/emailThread';

interface PendingEmailImportRowProps {
  connection: EmailThreadCompanyConnection;
  /** Only the all-companies queue passes this — a company name badge per row. */
  companyName?: string;
  actioning: boolean;
  onApprove: () => void;
  onReject: () => void;
  /** Opens the real thread (with full message content) in the main Messages pane — this row
   * only ever shows a summary, never the actual conversation, so reviewing content before
   * approving/rejecting always goes through here. */
  onOpenThread: () => void;
}

/** One row of a pending-email-import queue — shared by {@link PendingEmailImports} (single
 * company) and {@link PendingEmailImportsAllCompanies} (platform-admin, cross-company), which
 * differ only in whether a company badge is shown. Extracted so the modal redesign didn't
 * require maintaining two near-identical row markups in sync by hand. */
export function PendingEmailImportRow({
  connection,
  companyName,
  actioning,
  onApprove,
  onReject,
  onOpenThread,
}: PendingEmailImportRowProps) {
  const t = useTranslations('consultantMessages.imports.row');
  const relativeTime = useRelativeTime();
  const subject = connection.conversation_threads?.subject || t('noSubject');

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-white px-3 py-2">
      <button
        type="button"
        onClick={onOpenThread}
        className="min-w-0 flex-1 text-left"
        aria-label={t('openThread', { subject })}
      >
        {companyName !== undefined && (
          <div className="mb-0.5 flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-medium text-primary border-primary/30 bg-primary/5">
              {companyName}
            </Badge>
          </div>
        )}
        <p className="truncate text-sm font-medium hover:underline">{subject}</p>
        <p className="text-xs text-muted-foreground">{t('imported', { time: relativeTime(connection.created_at) })}</p>
        {connection.connectionCount > 1 && (
          <p
            className="mt-1 text-xs font-medium text-destructive"
            title={t('multiMailboxHint')}
          >
            {t('multiMailbox', { count: String(connection.connectionCount) })}
          </p>
        )}
      </button>
      <div className="flex shrink-0 items-center gap-1">
        <Badge variant="outline" className="mr-1 text-amber-700">
          {t('importedFromEmail')}
        </Badge>
        <Button
          size="sm"
          variant="outline"
          aria-label={t('reject')}
          disabled={actioning}
          onClick={onReject}
        >
          {actioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
        </Button>
        <Button size="sm" aria-label={t('approve')} disabled={actioning} onClick={onApprove}>
          {actioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
